<template>
  <BaseModal overlay-class="other_user_prof">
    <div class="content">
      <!-- User Profile Header -->
      <div class="trans_box">
        <div class="flex-row outer">
          <div class="flex-column left">
            <img
              :src="userProfile.avatar || './static/images/avatars/avatar_01.svg'"
              :alt="userProfile.name + ' profile'"
              @error="handleAvatarError"
            >
          </div>
          <div class="flex-column right">
            <p class="name">{{ userProfile.name || $_translations.value.modal_user_profile_details_anonymous }}</p>
            <div class="flex-row inner inner_trans_box">
              <img src="/static/images/prizes-leaderboards/points_1.svg" alt="Points">
              <p class="points">{{ userDetails?.totalPointsEarned || 0 }}</p>
              <p class="lbl">{{ $_translations.value.modal_user_profile_details_collected_points }}</p>
            </div>
          </div>
        </div>
      </div>

      <!-- Game Performance Section -->
      <h1>{{ $_translations.value.modal_user_profile_details_game_performance_title }}</h1>

      <div class="flex-row inner inner_trans_box bottom">
        <img :src="getLevelIcon(userDetails?.level)" alt="Level">
        <p class="points">{{ getLevelText(userDetails?.level) }}</p>
        <p class="lbl">{{ userDetails?.gamesToNextLevel || 0 }} {{ $_translations.value.modal_user_profile_details_games_to_level_up }}</p>
      </div>

      <div class="flex-row inner inner_trans_box bottom">
        <img src="/static/images/prizes-leaderboards/games_left_yes.svg" alt="Games Played">
        <p class="points">{{ userDetails?.gamesPlayed  || 0 }}</p>
        <p class="lbl">{{ $_translations.value.modal_user_profile_details_games_played }}</p>
      </div>

      <div class="flex-row inner inner_trans_box bottom">
        <img src="/static/images/prizes-leaderboards/correct.svg" alt="Correct Answers">
        <p class="points">{{ userStatistics?.correctQuestions || 0 }}</p>
        <p class="lbl">{{ $_translations.value.modal_user_profile_details_correct_answers }}</p>
      </div>

      <div class="flex-row inner inner_trans_box bottom">
        <img src="/static/images/prizes-leaderboards/achievements.svg" alt="Achievements">
        <p class="points">{{ userAchievements?.totalUnlockedAchievements || 0 }}</p>
        <p class="lbl">{{ $_translations.value.modal_user_profile_details_achievements }}</p>
      </div>
    </div>
  </BaseModal>

</template>


<script setup>
import { ref, onMounted, getCurrentInstance } from 'vue';
import BaseModal from '@/components/modal/BaseModal.vue';
import {
  apiRetrieveUserDetails,
  apiRetrieveUserAchievements,
  apiRetrieveUserStatistics,
} from '@/services/user.js';

const props = defineProps({
  isVisible: {
    type: Boolean,
    default: false,
  },
  userProfile: {
    type: Object,
    default: () => ({
      id: null,
      name: '',
      avatar: '',
      collectedPoints: 0,
      level: 1,
      gamesPlayed: 0,
      correctAnswers: 0,
      achievements: 0,
      msisdn: '', // Add msisdn to the default object
    }),
  },
});

const emit = defineEmits(['close', 'update:isVisible']);
const { proxy } = getCurrentInstance();

// API data state
const userDetails = ref(null);
const userStatistics = ref(null);
const userAchievements = ref(null);
const isLoading = ref(false);
const error = ref(null);

// Load all required data from APIs
const loadUserData = async () => {
  if (isLoading.value) return;

  isLoading.value = true;
  error.value = null;

  try {
    const msisdn = props.userProfile.msisdn || '';

    // Load all APIs in parallel
    const [detailsResponse, statisticsResponse, achievementsResponse] = await Promise.allSettled([
      apiRetrieveUserDetails({ msisdn }),
      apiRetrieveUserStatistics({ msisdn }),
      apiRetrieveUserAchievements({ msisdn }),
    ]);

    // Handle user details response
    if (detailsResponse.status === 'fulfilled') {
      userDetails.value = detailsResponse.value.parsedData;
    }

    // Handle statistics response
    if (statisticsResponse.status === 'fulfilled') {
      userStatistics.value = statisticsResponse.value.parsedData;
    }

    // Handle achievements response
    if (achievementsResponse.status === 'fulfilled') {
      userAchievements.value = achievementsResponse.value.parsedData;
    }

  } catch (error) {
    error.value = proxy.$_translations.value.modal_user_profile_details_error_loading_data;
  } finally {
    isLoading.value = false;
  }
};

// Close modal methods
const closeModal = () => {
  // Clean up
  document.removeEventListener('keydown', handleKeyDown);

  // Reset API data when closing
  userDetails.value = null;
  userStatistics.value = null;
  userAchievements.value = null;
  error.value = null;

  emit('update:isVisible', false);
  emit('close');
};

// Handle avatar image error
const handleAvatarError = (event) => {
  event.target.src = '/static/images/avatars/avatar_01.svg';
};

// Get level icon based on level
const getLevelIcon = (level) => {
  const levelIcons = {
    1: '/static/images/prizes-leaderboards/lvl1.svg',
    2: '/static/images/prizes-leaderboards/lvl2.svg',
    3: '/static/images/prizes-leaderboards/lvl3.svg',
    4: '/static/images/prizes-leaderboards/lvl4.svg',
    5: '/static/images/prizes-leaderboards/lvl5.svg',
    6: '/static/images/prizes-leaderboards/lvl6.svg',
  };
  return levelIcons[level] || levelIcons[1];
};

// Get level text
const getLevelText = (level) => {
  return `${ proxy.$_translations.value.modal_user_profile_details_level_prefix } ${ level || 1 }`;
};

// Handle ESC key to close modal
const handleKeyDown = (event) => {
  if (event.key === 'Escape' && props.isVisible) {
    closeModal();
  }
};

// Load data when component mounts
onMounted(async () => {
  // Setup keyboard listener
  document.addEventListener('keydown', handleKeyDown);

  // Load user data
  await loadUserData();
});
</script>
